import { enrichedModuleSchemaSchema, } from '../interfaces/enrichedModuleSchema.js';
import { EnrichedModel } from './EnrichedModel.js';
import { EnrichedRelation } from './EnrichedRelation.js';
import { pascalCaseToLabel, pascalToCamelCase } from '../utils/strings.js';
import { EnrichedColumn } from './EnrichedColumn.js';
import { isBelongsToRelation } from '../utils/relations.js';
import { fakerMethods } from '../interfaces/fakerMethods.js';
export class EnrichedModule {
    parentApp;
    name;
    displayName;
    icon;
    model;
    order;
    constructor(params, parentApp) {
        this.parentApp = parentApp;
        this.name = params.name;
        this.displayName = params.displayName;
        this.icon = params.icon;
        this.model =
            params.model instanceof EnrichedModel ? params.model : new EnrichedModel(params.model, this);
        this.model.parentModule = this;
        this.order = params.order;
    }
    /**
     * Creates an EnrichedModule instance directly from a JSON string.
     *
     * This method parses the provided JSON string and constructs a new EnrichedModule instance
     * using the parsed data. Note that this method does not perform schema validation;
     * it assumes the JSON is well-formed and matches the expected structure.
     *
     * @param json - A JSON string representing an enriched module object.
     * @returns An EnrichedModule instance created from the parsed JSON data.
     * @throws If the JSON is malformed or cannot be parsed.
     *
     * @example
     * ```typescript
     * const jsonString = '{"name":"User","displayName":"User","icon":"user","order":0,"model":{...}}';
     * const module = EnrichedModule.fromJSON(jsonString);
     * console.log(module.name); // "User"
     * ```
     */
    static fromJSON(json) {
        return new EnrichedModule(JSON.parse(json));
    }
    /**
     * Safely creates an EnrichedModule instance from a JSON string with schema validation.
     *
     * This method parses the provided JSON string and validates its structure against
     * the enriched module schema. If the JSON is invalid or does not conform to the
     * expected schema, an error is thrown. This provides type safety and ensures
     * data integrity.
     *
     * @param json - A JSON string representing an enriched module object.
     * @returns An EnrichedModule instance created from the validated JSON data.
     * @throws If the JSON is malformed or does not match the enriched module schema.
     *
     * @example
     * ```typescript
     * try {
     *   const module = EnrichedModule.fromJSONSafe(jsonString);
     *   console.log('Valid module:', module.name);
     * } catch (error) {
     *   console.error('Invalid JSON structure:', error.message);
     * }
     * ```
     */
    static fromJSONSafe(json) {
        return new EnrichedModule(enrichedModuleSchemaSchema.parse(JSON.parse(json)));
    }
    /**
     * Converts the EnrichedModule instance into a plain JavaScript object (POJO).
     *
     * This method transforms the EnrichedModule instance into a serializable plain object
     * suitable for transport, storage, or API communication. It strips away class methods
     * and returns only the data structure, including the converted model POJO.
     *
     * @returns A plain object representation of the EnrichedModule.
     *
     * @example
     * ```typescript
     * const module = new EnrichedModule({...});
     * const moduleData = module.toPOJO();
     * // moduleData is now a plain object that can be JSON.stringify'd
     * ```
     */
    toPOJO() {
        return {
            name: this.name,
            displayName: this.displayName,
            icon: this.icon,
            order: this.order,
            model: this.model.toPOJO(),
        };
    }
    /**
     * Serializes the EnrichedModule instance to a JSON string.
     *
     * This method converts the EnrichedModule instance to a plain object and then
     * serializes it as a JSON string, making it suitable for storage, transport,
     * or API communication.
     *
     * @returns A JSON string representation of the EnrichedModule.
     *
     * @example
     * ```typescript
     * const module = new EnrichedModule({...});
     * const jsonString = module.toJSON();
     * // Can be sent over HTTP or saved to file
     * ```
     */
    toJSON() {
        return JSON.stringify(this.toPOJO());
    }
    /**
     * Generates a human-readable display name from a module name.
     *
     * This static method converts a PascalCase module name into a space-separated
     * label format suitable for user interfaces. For example, "UserProfile" becomes
     * "User Profile".
     *
     * @param name - The module name in PascalCase format.
     * @returns A formatted display name with spaces between words.
     *
     * @example
     * ```typescript
     * const displayName = EnrichedModule.nameToDisplayName('UserProfile');
     * console.log(displayName); // "User Profile"
     * ```
     */
    static nameToDisplayName(name) {
        return pascalCaseToLabel(name);
    }
    /**
     * Sets the module name and updates all related naming properties and references.
     *
     * This method updates the module's name, generates the corresponding display name,
     * and updates the underlying model's name to maintain consistency. It also updates
     * any relations in other modules that reference this module by its old name.
     * The module registry is updated to reflect the name change.
     *
     * @param name - The new name for the module (should be in PascalCase).
     * @param registry - The module registry to update relations in (defaults to this.moduleRegistry).
     * @throws EmptyModuleNameError if the provided name is an empty string.
     * @throws DuplicateModuleError if a module with the new name already exists in the registry.
     *
     * @example
     * ```typescript
     * const module = new EnrichedModule({name: 'OldName', ...});
     * module.setNames('NewName'); // Updates name, displayName, model name, and all references
     * ```
     *
     * TODO: We should support name in any case style
     */
    setNames(name, options = {
        preventAppUpdate: false,
    }) {
        const trimmedName = name.trim();
        if (trimmedName === '') {
            throw new EmptyModuleNameError('The module name must contain at least 1 letter', name);
        }
        if (this.parentApp?.moduleRegistry.has(trimmedName)) {
            throw new DuplicateModuleError('Module with the same name already added to registry', trimmedName);
        }
        const oldName = this.name;
        this.model.setNames(trimmedName);
        this.name = trimmedName;
        this.displayName = EnrichedModule.nameToDisplayName(trimmedName);
        // Update relations that reference this model
        for (const [, relatedModule] of this.parentApp?.moduleRegistry ?? new Map()) {
            for (const relationName of Object.keys(relatedModule.model.relations)) {
                const relation = relatedModule.model.relations[relationName];
                if (relation.model === oldName) {
                    relation.model = name;
                }
            }
        }
        if (options.preventAppUpdate)
            return;
        // Update registry entry
        this.parentApp?.moduleRegistry.delete(oldName);
        this.parentApp?.moduleRegistry.set(trimmedName, this);
    }
    /**
     * Removes a column from the module's model and adjusts related properties.
     *
     * This method deletes the specified column from the model's columns collection.
     * If the removed column was the model's title column, it automatically sets
     * the title to the first remaining column (or empty string if no columns remain).
     * It also adjusts the order property of subsequent columns to maintain
     * proper sequential ordering.
     *
     * @param name - The name of the column to remove.
     * @throws ColumnNotFoundError if no column with the specified name exists.
     *
     * @example
     * ```typescript
     * module.removeColumn('obsoleteField');
     * // Column is removed, title updated if necessary, orders resequenced
     * ```
     */
    removeColumn(name) {
        const column = this.model.columns[name];
        if (!column) {
            throw new ColumnNotFoundError('Column with the provided name not found in model', name);
        }
        const removedOrder = column.order;
        delete this.model.columns[name];
        if (this.model.title === name) {
            this.model.title = Object.keys(this.model.columns)[0] ?? '';
        }
        // Adjust orders of columns that come after the removed column
        for (const remainingColumn of Object.values(this.model.columns)) {
            if (remainingColumn.order > removedOrder) {
                remainingColumn.order--;
            }
        }
    }
    /**
     * Adds a new column to the module's model with automatic order management.
     *
     * This method adds a column to the model, ensuring no duplicate column names exist.
     * It automatically adjusts the order property of existing columns to accommodate
     * the new column at its specified position. If the model has no title set and
     * this is the first column, it becomes the title column.
     *
     * @param column - The column instance or constructor parameters to add.
     * @param position - Optional position to place the column at. If provided, overrides the column's order property.
     * @returns The EnrichedModule instance (for method chaining).
     * @throws DuplicateColumnError if a column with the same name already exists.
     * @throws ColumnOrderGapError if the column's order or position would create a gap in column order.
     *
     * @example
     * ```typescript
     * const newColumn = new EnrichedColumn({name: 'email', type: 'string', order: 1});
     * module.addColumn(newColumn);
     * // Column added, existing column orders adjusted accordingly
     *
     * // Or specify position explicitly
     * module.addColumn(newColumn, 0); // Places column at position 0 regardless of its order property
     * ```
     */
    addColumn(column, position) {
        if (this.model.columns[column.name]) {
            throw new DuplicateColumnError('Column with the same name already added to model', column.name);
        }
        // Use position parameter if provided, otherwise use column's order property
        const targetOrder = position !== undefined ? position : column.order;
        // Validate that target order doesn't create a gap
        const currentColumnCount = Object.keys(this.model.columns).length;
        if (targetOrder > currentColumnCount) {
            throw new ColumnOrderGapError(`Cannot add column at order ${targetOrder}. This would create a gap in column order. Maximum allowed order is ${currentColumnCount}.`, targetOrder, currentColumnCount);
        }
        this.setColumn(column, position);
        return this;
    }
    /**
     * Sets or replaces a column in the module's model with automatic order management.
     *
     * This method adds or replaces a column in the model, automatically adjusting
     * the order property of other columns to accommodate the new column's position.
     * Unlike addColumn, this method will overwrite existing columns with the same name.
     * If the model has no title set and this is the first column, it becomes the title column.
     *
     * @param column - The column instance or constructor parameters to set.
     * @param position - Optional position to place the column at. If provided, overrides the column's order property.
     * @throws ColumnOrderGapError if the column's order or position would create a gap in column order.
     *
     * @example
     * ```typescript
     * const column = new EnrichedColumn({name: 'updatedField', type: 'string', order: 2});
     * module.setColumn(column);
     * // Column set/replaced, other column orders adjusted
     *
     * // Or specify position explicitly
     * module.setColumn(column, 0); // Places column at position 0 regardless of its order property
     * ```
     *
     * WARNING: Will overwrite existing columns with the same name.
     */
    setColumn(column, position) {
        // Use position parameter if provided, otherwise use column's order property
        const targetOrder = position !== undefined ? position : column.order;
        // Validate that target order doesn't create a gap
        const currentColumnCount = Object.keys(this.model.columns).length;
        if (targetOrder > currentColumnCount) {
            throw new ColumnOrderGapError(`Cannot set column at order ${targetOrder}. This would create a gap in column order. Maximum allowed order is ${currentColumnCount}.`, targetOrder, currentColumnCount);
        }
        const columnInstance = column instanceof EnrichedColumn ? column : new EnrichedColumn(column);
        // Set the column's order to the target order
        columnInstance.order = targetOrder;
        // Adjust orders of existing columns that have order >= targetOrder
        for (const existingColumn of Object.values(this.model.columns)) {
            if (existingColumn.order >= targetOrder) {
                existingColumn.order++;
            }
        }
        this.model.columns[column.name] = columnInstance;
        columnInstance.parentModel = this.model;
        if (this.model.title === '')
            this.model.title = column.name;
    }
    /**
     * Moves a column to a new position within the module's model and updates order properties.
     *
     * This method reorders the columns by moving the specified column to the given position.
     * It updates the `order` property of all affected columns to ensure proper sequential
     * ordering. Columns between the old and new positions are shifted accordingly.
     *
     * @param name - The name of the column to move.
     * @param position - The target position (order) to move the column to.
     * @throws ColumnNotFoundError if no column with the specified name exists.
     * @throws ColumnOrderGapError if the position would create a gap in column order.
     *
     * @example
     * ```typescript
     * module.moveColumn('email', 0); // Move 'email' column to first position
     * // All affected column orders are automatically updated
     * ```
     */
    moveColumn(name, position) {
        const column = this.model.columns[name];
        if (!column) {
            throw new ColumnNotFoundError('Column with the provided name not found in model', name);
        }
        // Validate that position doesn't create a gap
        const currentColumnCount = Object.keys(this.model.columns).length;
        if (position >= currentColumnCount) {
            throw new ColumnOrderGapError(`Cannot move column to position ${position}. This would create a gap in column order. Maximum allowed position is ${currentColumnCount - 1}.`, position, currentColumnCount);
        }
        const columnsInOrder = Object.values(this.model.columns).toSorted((a, b) => a.order - b.order);
        // The position of the column we start moving from
        const startColumnOrder = column.order;
        // The amount of positions we need to move the column
        const moveAmount = Math.abs(column.order - position);
        // The direction we are moving the column
        const direction = column.order < position ? 'down' : 'up';
        // Loop over all the columns we need to move and move them one down or one up
        for (let i = 1; i <= moveAmount; i++) {
            if (direction === 'down') {
                columnsInOrder[startColumnOrder + i].order -= 1;
            }
            else {
                columnsInOrder[startColumnOrder - i].order += 1;
            }
        }
        // Move the provided column to the new position
        column.order = position;
    }
    /**
     * Creates a default empty column with basic configuration.
     *
     * This method generates a new EnrichedColumn instance with default settings
     * suitable for user input. The column is created with empty name and display names,
     * string type, a sentence faker method, and an order that places it at the end
     * of the current columns list.
     *
     * @returns A new EnrichedColumn instance with default empty configuration.
     *
     * @example
     * ```typescript
     * const emptyColumn = module.makeDefaultEmptyColumn();
     * // User can then modify the column properties before adding it
     * emptyColumn.setNames('newField');
     * module.addColumn(emptyColumn);
     * ```
     */
    makeDefaultEmptyColumn() {
        return new EnrichedColumn({
            name: '',
            displayName: '',
            displayNamePlural: '',
            type: 'string',
            fakerMethod: fakerMethods.sentence,
            searchable: false,
            order: Object.keys(this.model.columns).length,
        });
    }
    /**
     * Adds a bidirectional relation between this module and another module.
     *
     * This method creates a relation on this module and its corresponding inverse
     * relation on the related module. It ensures both relations are properly
     * configured and adds any necessary foreign key columns for BelongsTo relations.
     * The method validates that no relations with the same name already exist.
     *
     * @param params - Object containing the relation and its inverse relation.
     * @param params.relation - The relation to add to this module.
     * @param params.inverse - The inverse relation to add to the related module.
     * @param params.skipForeignKey - Optional flag to skip adding foreign key columns for BelongsTo relations.
     * @param registry - The module registry to validate against (defaults to this.moduleRegistry).
     * @throws RelationAlreadyExistsError if a relation with the same name already exists.
     *
     * @example
     * ```typescript
     * const userModule = registry.get('User');
     * const {relation, inverse} = userModule.makeRelation({relationType: 'HasMany', model: 'Post'});
     * userModule.addRelation({relation, inverse});
     *
     * // Skip foreign key creation
     * userModule.addRelation({relation, inverse, skipForeignKey: true});
     * ```
     */
    addRelation(params) {
        if (this.model.relations[params.relation.name]) {
            throw new RelationAlreadyExistsError('Relation with the same name already exists on this model. If you want to overwrite the existing relation use `EnrichedModule.setRelation() instead.`', this.parentApp?.moduleRegistry, params.relation, params.inverse);
        }
        this.setRelation(params);
    }
    /**
     * Sets or replaces a bidirectional relation between this module and another module.
     *
     * This method creates or overwrites a relation on this module and its corresponding
     * inverse relation on the related module. It handles the removal of existing relations
     * if they exist, validates the new relation configuration, and adds any necessary
     * foreign key columns for BelongsTo relations. Unlike addRelation, this method
     * allows overwriting existing relations.
     *
     * @param registry - The module registry containing all modules.
     * @param params - Object containing the relation and its inverse relation.
     * @param params.relation - The relation to set on this module.
     * @param params.inverse - The inverse relation to set on the related module.
     * @param params.skipForeignKey - Optional flag to skip adding foreign key columns for BelongsTo relations.
     * @throws ModuleDoesntExistInRegistryError if the related module is not found in the registry.
     * @throws InverseRelationHasInvalidInverseNameError if the inverse relation's inverse name doesn't match the relation name.
     * @throws InverseRelationHasInvalidModelError if the inverse relation's model doesn't match this module's name.
     *
     * @example
     * ```typescript
     * const {relation, inverse} = userModule.makeRelation({relationType: 'HasMany', model: 'Post'});
     * userModule.setRelation(registry, {relation, inverse});
     * // Relation set/replaced, foreign keys added if needed
     *
     * // Skip foreign key creation
     * userModule.setRelation(registry, {relation, inverse, skipForeignKey: true});
     * ```
     */
    setRelation(params) {
        const relatedModule = this.parentApp?.moduleRegistry.get(params.relation.model);
        if (!relatedModule) {
            throw new ModuleDoesntExistInRegistryError("Can't set relation as the related module can't be found in registry (you can only add relations to modules registered in the registry)", this.parentApp?.moduleRegistry, params.relation);
        }
        if (params.inverse.inverse !== params.relation.name) {
            throw new InverseRelationHasInvalidInverseNameError("Can't set relation as provided inverse relation has a different inverse relation name than the provided relation name", this.parentApp?.moduleRegistry, params.relation, params.inverse);
        }
        if (params.inverse.model !== this.model.name) {
            throw new InverseRelationHasInvalidModelError("Can't set relation as provided inverse relation has a different model than the provided module name", this.parentApp?.moduleRegistry, params.relation, params.inverse);
        }
        // Check if overwriting existing relation
        const existingRelation = this.model.relations[params.relation.name];
        if (existingRelation) {
            const existingRelatedModule = this.parentApp?.moduleRegistry.get(existingRelation.model);
            if (!existingRelatedModule) {
                throw new ModuleDoesntExistInRegistryError("The related module doesn't exist in registry. This is a fatal error, as it means that the entire app schema is invalid. This was likely not caused by this function but is a pre-existing problem of the provided schema. Report and fix the bug.", this.parentApp?.moduleRegistry, existingRelation);
            }
            const existingInverseRelation = existingRelatedModule.model.relations[existingRelation.inverse];
            if (!existingInverseRelation) {
                throw new InverseRelationDoesntExistOnRelatedModelError("The inverse relation for the relation you're trying to remove doesn't exist. This is a fatal error, as it means that the entire app schema is invalid. This was likely not caused by this function but is a pre-existing problem of the provided schema. Report and fix the bug.", this.parentApp?.moduleRegistry, existingRelation, existingInverseRelation);
            }
            // Then remove the existing relations
            this.removeRelation(params.relation.name);
        }
        this.model.relations[params.relation.name] = params.relation;
        this.model.relations[params.relation.name].parentModel = this.model;
        relatedModule.model.relations[params.inverse.name] = params.inverse;
        relatedModule.model.relations[params.inverse.name].parentModel = relatedModule.model;
        if (params.skipForeignKey)
            return;
        if (isBelongsToRelation(params.relation)) {
            this.addColumn(params.relation.makeForeignKeyForBelongsToRelation());
        }
        else if (isBelongsToRelation(params.inverse)) {
            relatedModule.addColumn(params.inverse.makeForeignKeyForBelongsToRelation());
        }
    }
    /**
     * Removes a bidirectional relation and associated foreign key columns.
     *
     * This method removes the specified relation from this module and its corresponding
     * inverse relation from the related module. It also removes any foreign key columns
     * that were created for BelongsTo relations. The method validates that both the
     * relation and its inverse exist before attempting removal.
     *
     * @param registry - The module registry containing all modules.
     * @param name - The name of the relation to remove from this module.
     * @throws ModuleDoesntExistInRegistryError if the related module is not found in the registry.
     * @throws InverseRelationDoesntExistOnRelatedModelError if the inverse relation is not found on the related module.
     *
     * @example
     * ```typescript
     * module.removeRelation(registry, 'posts');
     * // Relation removed from both modules, foreign key columns deleted
     * ```
     */
    removeRelation(name) {
        const existingRelation = this.model.relations[name];
        const relatedModule = this.parentApp?.moduleRegistry.get(existingRelation.model);
        if (!relatedModule) {
            throw new ModuleDoesntExistInRegistryError("The related module doesn't exist in registry. This is a fatal error, as it means that the entire app schema is invalid. This was likely not caused by this function but is a pre-existing problem of the provided schema. Report and fix the bug.", this.parentApp?.moduleRegistry, existingRelation);
        }
        const existingInverseRelation = relatedModule.model.relations[existingRelation.inverse];
        if (!existingInverseRelation) {
            throw new InverseRelationDoesntExistOnRelatedModelError("The inverse relation for the relation you're trying to remove doesn't exist. This is a fatal error, as it means that the entire app schema is invalid. This was likely not caused by this function but is a pre-existing problem of the provided schema. Report and fix the bug.", this.parentApp?.moduleRegistry, existingRelation, existingInverseRelation);
        }
        if (isBelongsToRelation(existingRelation)) {
            const column = Object.values(this.model.columns).find((col) => col.forRelation === existingRelation.name);
            if (column) {
                delete this.model.columns[column.name];
            }
        }
        else if (isBelongsToRelation(existingInverseRelation)) {
            const column = Object.values(relatedModule.model.columns).find((col) => col.forRelation === existingInverseRelation.name);
            if (column) {
                delete relatedModule.model.columns[column.name];
            }
        }
        delete relatedModule.model.relations[existingRelation.inverse];
        delete this.model.relations[name];
    }
    /**
     * Creates a relation and its corresponding inverse relation with automatic naming.
     *
     * This method generates a pair of relations based on the specified relation type
     * and target model. It automatically determines appropriate names for both the
     * relation and its inverse based on the relation type and model names. The method
     * handles the creation of properly configured EnrichedRelation instances with
     * correct inverse references.
     *
     * @param params - Configuration object for the relation.
     * @param params.relationType - The type of relation (excluding 'BelongsTo' which is auto-generated as inverse).
     * @param params.model - The name of the related model/module.
     * @param params.inverse - Optional custom name for the inverse relation.
     * @param params.name - Optional custom name for the relation.
     * @returns Object containing both the relation and its inverse relation.
     *
     * @example
     * ```typescript
     * const {relation, inverse} = userModule.makeRelation({
     *   relationType: 'HasMany',
     *   model: 'Post'
     * });
     * // Creates 'posts' relation on User and 'user' inverse relation on Post
     * ```
     */
    makeRelation(params) {
        let name = null;
        if (!params.name) {
            if (['HasOne'].includes(params.relationType)) {
                name = pascalToCamelCase(params.model);
            }
            else {
                name = pascalToCamelCase(params.model) + 's';
            }
        }
        else {
            name = params.name;
        }
        let inverseName = null;
        if (!params.inverse) {
            if (['HasOne', 'HasMany'].includes(params.relationType)) {
                inverseName = pascalToCamelCase(this.model.name);
            }
            else {
                inverseName = pascalToCamelCase(this.model.name) + 's';
            }
        }
        else {
            inverseName = params.inverse;
        }
        const relation = new EnrichedRelation({
            relationType: params.relationType,
            model: params.model,
            inverse: inverseName,
            name: name,
            displayName: EnrichedRelation.nameToDisplayName(name),
        });
        const inverseRelation = this.#createInverseRelation(name, relation);
        return {
            relation,
            inverse: inverseRelation,
        };
    }
    #createInverseRelation(relationName, relation) {
        const inverseRelationMap = {
            BelongsTo: 'HasMany',
            HasMany: 'BelongsTo',
            HasOne: 'BelongsTo',
            BelongsToMany: 'BelongsToMany',
        };
        const inverseName = relation.inverse;
        return new EnrichedRelation({
            relationType: inverseRelationMap[relation.relationType],
            model: this.model.name,
            inverse: relationName,
            name: inverseName,
            displayName: EnrichedRelation.nameToDisplayName(inverseName),
        });
    }
}
// Error classes from Module
export class RelationAlreadyExistsError extends Error {
    registry;
    relation;
    inverseRelation;
    constructor(message, registry, relation, inverseRelation) {
        super(message);
        this.registry = registry;
        this.relation = relation;
        this.inverseRelation = inverseRelation;
        this.name = 'RelationAlreadyExistsError';
    }
}
export class ModuleDoesntExistInRegistryError extends Error {
    registry;
    relation;
    constructor(message, registry, relation) {
        super(message);
        this.registry = registry;
        this.relation = relation;
        this.name = 'ModuleDoesntExistInRegistryError';
    }
}
export class InverseRelationHasInvalidInverseNameError extends Error {
    registry;
    relation;
    inverseRelation;
    constructor(message, registry, relation, inverseRelation) {
        super(message);
        this.registry = registry;
        this.relation = relation;
        this.inverseRelation = inverseRelation;
        this.name = 'InverseRelationHasInvalidInverseNameError';
    }
}
export class InverseRelationHasInvalidModelError extends Error {
    registry;
    relation;
    inverseRelation;
    constructor(message, registry, relation, inverseRelation) {
        super(message);
        this.registry = registry;
        this.relation = relation;
        this.inverseRelation = inverseRelation;
        this.name = 'InverseRelationHasInvalidModelError';
    }
}
export class InverseRelationDoesntExistOnRelatedModelError extends Error {
    registry;
    relation;
    inverseRelation;
    constructor(message, registry, relation, inverseRelation) {
        super(message);
        this.registry = registry;
        this.relation = relation;
        this.inverseRelation = inverseRelation;
        this.name = 'InverseRelationDoesntExistOnRelatedModelError';
    }
}
export class DuplicateModuleError extends Error {
    name;
    constructor(message, name) {
        super(message);
        this.name = name;
        this.name = 'DuplicateModuleError';
    }
}
export class EmptyModuleNameError extends Error {
    name;
    constructor(message, name) {
        super(message);
        this.name = name;
        this.name = 'EmptyModuleNameError';
    }
}
export class DuplicateColumnError extends Error {
    name;
    constructor(message, name) {
        super(message);
        this.name = name;
        this.name = 'DuplicateColumnError';
    }
}
export class ColumnNotFoundError extends Error {
    name;
    constructor(message, name) {
        super(message);
        this.name = name;
        this.name = 'ColumnNotFoundError';
    }
}
export class ColumnOrderGapError extends Error {
    position;
    currentColumnCount;
    constructor(message, position, currentColumnCount) {
        super(message);
        this.position = position;
        this.currentColumnCount = currentColumnCount;
        this.name = 'ColumnOrderGapError';
    }
}
